import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { cn } from '../../../utils/cn';

const InteractiveReportBuilder = ({ onClose, onGenerate, selectedFormat }) => {
  const [reportName, setReportName] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [filters, setFilters] = useState({});
  const [visualization, setVisualization] = useState('table');

  const availableFields = [
    { value: 'transaction_id', label: 'Transaction ID', category: 'Transaction' },
    { value: 'merchant_name', label: 'Merchant Name', category: 'Merchant' },
    { value: 'amount', label: 'Amount', category: 'Financial' },
    { value: 'payment_method', label: 'Payment Method', category: 'Transaction' },
    { value: 'status', label: 'Status', category: 'Transaction' },
    { value: 'created_at', label: 'Created At', category: 'Transaction' },
    { value: 'risk_score', label: 'Risk Score', category: 'Fraud' },
    { value: 'settlement_date', label: 'Settlement Date', category: 'Financial' },
    { value: 'fees', label: 'Fees', category: 'Financial' },
    { value: 'net_amount', label: 'Net Amount', category: 'Financial' }
  ];

  const visualizationOptions = [
    { value: 'table', label: 'Data Table', icon: 'Table' },
    { value: 'chart', label: 'Chart View', icon: 'BarChart3' },
    { value: 'pivot', label: 'Pivot Table', icon: 'Grid' },
    { value: 'summary', label: 'Summary Cards', icon: 'LayoutGrid' }
  ];

  const handleFieldToggle = (field) => {
    setSelectedFields(prev =>
      prev?.includes(field)
        ? prev?.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const handleGenerate = () => {
    const report = {
      name: reportName || 'Custom Report',
      fields: selectedFields,
      filters,
      visualization,
      format: selectedFormat
    };
    onGenerate(report);
    onClose();
  };

  const groupedFields = availableFields?.reduce((acc, field) => {
    if (!acc?.[field?.category]) {
      acc[field?.category] = [];
    }
    acc?.[field?.category]?.push(field);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Wand2" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">
                Interactive Report Builder
              </h2>
              <p className="text-sm text-muted-foreground">
                Create custom reports with drag-and-drop field selection
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Report Name */}
          <div className="mb-6">
            <Input
              label="Report Name"
              placeholder="Enter report name"
              value={reportName}
              onChange={(e) => setReportName(e?.target?.value)}
              required
            />
          </div>

          {/* Field Selection */}
          <div className="mb-6">
            <label className="text-sm font-medium text-foreground mb-3 block">
              Select Fields
              <span className="text-muted-foreground ml-2">
                ({selectedFields?.length} selected)
              </span>
            </label>
            <div className="space-y-4">
              {Object?.entries(groupedFields)?.map(([category, fields]) => (
                <div key={category}>
                  <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                    {category}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {fields?.map((field) => (
                      <button
                        key={field?.value}
                        onClick={() => handleFieldToggle(field?.value)}
                        className={cn(
                          'flex items-center gap-2 p-3 rounded-lg border-2 transition-all duration-200 text-left',
                          selectedFields?.includes(field?.value)
                            ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                        )}
                      >
                        <div
                          className={cn(
                            'w-4 h-4 rounded border-2 flex items-center justify-center',
                            selectedFields?.includes(field?.value)
                              ? 'border-primary bg-primary' :'border-border'
                          )}
                        >
                          {selectedFields?.includes(field?.value) && (
                            <Icon name="Check" size={12} className="text-white" />
                          )}
                        </div>
                        <span className="text-sm text-foreground">
                          {field?.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Visualization Type */}
          <div className="mb-6">
            <label className="text-sm font-medium text-foreground mb-3 block">
              Visualization Type
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {visualizationOptions?.map((option) => (
                <button
                  key={option?.value}
                  onClick={() => setVisualization(option?.value)}
                  className={cn(
                    'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all duration-200',
                    visualization === option?.value
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                  )}
                >
                  <Icon
                    name={option?.icon}
                    size={24}
                    className={cn(
                      visualization === option?.value
                        ? 'text-primary' :'text-muted-foreground'
                    )}
                  />
                  <span
                    className={cn(
                      'text-sm font-medium',
                      visualization === option?.value
                        ? 'text-primary' :'text-foreground'
                    )}
                  >
                    {option?.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Export Format Info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-900 font-medium mb-1">
                  Export Format: {selectedFormat?.toUpperCase()}
                </p>
                <p className="text-xs text-blue-700">
                  Report will be generated in {selectedFormat?.toUpperCase()} format with selected fields and visualization.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            iconName="Play"
            iconPosition="left"
            onClick={handleGenerate}
            disabled={!reportName || selectedFields?.length === 0}
          >
            Generate Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InteractiveReportBuilder;